import { getBrowseById } from "#skyward_browseList";
import { type BrowseIdentifier, getPreferences, setPreferences } from "./skyward.ui.browsePreferences";
import { Browse } from "./types";

export interface SeekInfo
{
    seekId: number | null;
    prevSeekId: number | null;
    nextSeekId: number | null;
    quickFilterOpen: boolean | null;
}

/**
* Get the persisted seek info for a browse
*
* @param browse - The string name or browse object to get seek info for
* @return The seek info from browsePrefernces
*/
export function get(browse: BrowseIdentifier): SeekInfo | undefined
{
    const prefData = getPreferences(browse);
    return getSeekInfoFromObject(prefData);
};

/**
* Persist the seek info for a browse, if any.  Then return the browse's current seek info.  This is meant to be used internally by browse.js.
*
* @param browse - The string name or browse object the seek info is associated with
* @param data - An object containing seek info to return or persist
* @return The seek info either that was passed in, or that has been persisted for the browse
*/
export function setAndGet(browse: BrowseIdentifier, data: Partial<SeekInfo>): SeekInfo | undefined
{
    const seekInfo = getSeekInfoFromObject(data);

    if (!shouldSeekInfoBeTracked(browse))
    {
        return seekInfo;
    }
    if (!seekInfo)
    {
        return get(browse);
    }
    if (seekInfo.seekId == null || isNaN(Number(seekInfo.seekId)))
    {
        clear(browse);
        return undefined;
    }
    setPreferences(browse, seekInfo);
    return seekInfo;
};

/**
* Remove the seek info for a browse.
* @param browse - The string name or browse object to remove seek info for
*/
export function clear(browse: BrowseIdentifier): void
{
    if (!shouldSeekInfoBeTracked(browse))
    {
        return;
    }
    setPreferences(browse, {
        seekId: null,
        prevSeekId: null,
        nextSeekId: null,
        quickFilterOpen: null
    });
};

/**
* Persist the selected row primary keys for the passed in browse
*
* @param browse - The browse object to save seek info for
* @param seekInfo - Optional seek info to save
* @return The seek info that was saved in browsePrefernces
*/
export function save(browse: Browse, seekInfo?: Partial<SeekInfo>): SeekInfo | undefined
{
    if (!shouldSeekInfoBeTracked(browse))
    {
        return undefined;
    }

    if (seekInfo !== undefined)
    {
        return setAndGet(browse, seekInfo);
    }

    const hasPrimaryKeys = Array.isArray(browse.primaryKeys);
    const row = typeof browse.currentRow === "number" && Number.isInteger(browse.currentRow) ? browse.currentRow : undefined;

    const data: Partial<SeekInfo> = {
        seekId: hasPrimaryKeys && row !== undefined && Array.isArray(browse.primaryKeys) && typeof browse.primaryKeys[row] === "number"
            ? browse.primaryKeys[row]
            : undefined,
        prevSeekId: hasPrimaryKeys && row !== undefined && Array.isArray(browse.primaryKeys) && typeof browse.primaryKeys[row - 1] === "number"
            ? browse.primaryKeys[row - 1]
            : undefined,
        nextSeekId: hasPrimaryKeys && row !== undefined && Array.isArray(browse.primaryKeys) && typeof browse.primaryKeys[row + 1] === "number"
            ? browse.primaryKeys[row + 1]
            : undefined
    };

    return setAndGet(browse, data);
};

/**
* Return the seek info of an object.
* If seekId is not specified, the default seek info will be returned.
*
* @param sourceObject - An object that may contain seek info
* @return An object that contains the seek info properties from the sourceObject
*/
function getSeekInfoFromObject(sourceObject: any): SeekInfo | undefined
{
    if (!sourceObject || sourceObject.seekId == null)
    {
        return undefined;
    }
    return {
        seekId: sourceObject.seekId,
        prevSeekId: sourceObject.prevSeekId,
        nextSeekId: sourceObject.nextSeekId,
        quickFilterOpen: sourceObject.quickFilterOpen
    };
};

/**
* Return whether or not seek info should be tracked for the provided browse
* If seekId is not specified, the default seek info will be returned.
*
* @param browseParam - The string name or browse object to check
* @return True when the browse should track seek info
*/
function shouldSeekInfoBeTracked(browseParam: BrowseIdentifier): boolean
{
    let browse: any;
    if (typeof browseParam === "string")
    {
        browse = getBrowseById(browseParam);
    }
    else
    {
        browse = browseParam;
    }

    // TODO: PR 4471842 should make it so browse.isInDetailsPanel is more reliable, so checking browse.id.indexOf("detailsPanel_") should no longer be necessary.
    if (!browse || $.isEmptyObject(browse) || !browse.trackSeekInfo || browse.isInDetailsPanel || (typeof browse.id === "string" && browse.id.indexOf("detailsPanel_") === 0))
    {
        return false;
    }
    // -1 is used as a fallback for on-the-fly browses in DefaultBrowseScriptRenderer
    if (browse.browseId === -1)
    {
        return false;
    }
    return true;
};
